import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from "react";
import axios from "axios";
import * as Constants from '../constants';

const ConfigContext = createContext({});

// Create a simple axios instance for config calls to avoid dependency on useApi
const configAxios = axios.create({
    baseURL: Constants.API_HOST,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
    timeout: 2000 // 2 second timeout to prevent hanging in production
});

function ConfigProvider({ children }) {
    const [config, setConfig] = useState({
        featureFlags: {},
        appSettings: {},
        maxUploadSizeMb: 10 // Default value
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadConfig = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await configAxios.get(Constants.CONFIG);
            const data = response.data;
            
            // Process upload limits
            const maxFileSizeMb = data.upload_limits?.max_file_size 
                ? data.upload_limits.max_file_size / 1000000  // Convert bytes to MB
                : 10; // Default to 10MB if not provided
            
            setConfig({
                featureFlags: data.feature_flags || {},
                appSettings: data.app_settings || {},
                maxUploadSizeMb: maxFileSizeMb,
                ...data // Include any other config data
            });
        } catch (error) {
            console.error('Failed to load app config:', error);
            
            // Provide more specific error message for timeouts
            let errorMessage = 'Failed to load configuration';
            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                errorMessage = 'Configuration request timed out';
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setError(errorMessage);
            
            // Set fallback configuration
            setConfig({
                featureFlags: {},
                appSettings: {},
                maxUploadSizeMb: 10 // Default to 10MB
            });
        } finally {
            setIsLoading(false);
        }
    };

    const refetchConfig = useCallback(() => {
        loadConfig();
    }, []);

    useEffect(() => {
        loadConfig();
    }, []);

    const value = useMemo(() => ({
        config,
        isLoading,
        error,
        refetchConfig
    }), [config, isLoading, error, refetchConfig]);

    return (
        <ConfigContext.Provider value={value}>
            {children}
        </ConfigContext.Provider>
    );
}

// Custom hook to easily access config
export function useConfig() {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
}

export { ConfigProvider, ConfigContext };